/**
 * Umi Request 全局配置：业务错误码、Token 自动刷新、401 跳转登录。
 *
 * Token 存取见 services/auth.ts；刷新接口见 services/ant-design-pro/api.refreshUserToken。
 * 落地页与 /user/login 在会话失效时不强制跳转（isPublicPath）。
 */
import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message, notification } from 'antd';
import { reportMonitoringEvent } from '@/monitoring';
import { refreshUserToken } from '@/services/ant-design-pro/api';
import {
  clearUserTokens,
  getUserAccessToken,
  getUserRefreshToken,
  isUserAccessTokenExpired,
  isUserRefreshTokenExpired,
  redirectToUserLogin,
} from '@/services/auth';

let refreshingPromise: Promise<boolean> | null = null;
let redirectingToLogin = false;

const isPublicPath = (pathname = history.location.pathname) =>
  pathname === '/' || pathname === '/user/login' || pathname.startsWith('/landing');

const handleUserSessionExpired = (
  redirect = `${history.location.pathname}${history.location.search}`,
) => {
  clearUserTokens();
  if (isPublicPath()) {
    return;
  }
  if (redirectingToLogin) {
    return;
  }
  redirectingToLogin = true;
  redirectToUserLogin(redirect);
  window.setTimeout(() => {
    redirectingToLogin = false;
  }, 300);
};

const ensureFreshUserAccessToken = async () => {
  if (!getUserRefreshToken() || isUserRefreshTokenExpired()) {
    handleUserSessionExpired();
    return false;
  }

  if (!isUserAccessTokenExpired()) {
    return true;
  }

  if (!refreshingPromise) {
    refreshingPromise = refreshUserToken({ skipErrorHandler: true })
      .then((payload) => Boolean(payload?.accessToken))
      .catch(() => {
        handleUserSessionExpired();
        return false;
      })
      .finally(() => {
        refreshingPromise = null;
      });
  }

  return refreshingPromise;
};

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  code: number;
  message: string;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

const createBizError = (
  code: number,
  errorMessage: string,
  data?: any,
) => {
  const error: any = new Error(errorMessage);
  error.name = 'BizError';
  error.info = {
    errorCode: code,
    errorMessage,
    showType:ErrorShowType.ERROR_MESSAGE,
    data,
  };
  return error;
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const {
        code,
        data,
        message: errorMessage,
      } = res as unknown as ResponseStructure;
      if (code !== 0) {
        throw createBizError(code, errorMessage, data);
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          if (errorCode === 40100 || errorCode === 40101) {
            handleUserSessionExpired();
          }
          void reportMonitoringEvent({
            eventType: 'http_error',
            eventName: 'request_business_error',
            level:
              errorCode === 40100 || errorCode === 40101 ? 'warn' : 'error',
            page: history.location.pathname,
            route: history.location.pathname,
            message: errorMessage,
            extra: {
              errorCode,
            },
          });
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              handleUserSessionExpired();
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        if (error.response.status === 401) {
          handleUserSessionExpired();
        }
        void reportMonitoringEvent({
          eventType: 'http_error',
          eventName: 'request_response_error',
          level: 'error',
          page: history.location.pathname,
          route: history.location.pathname,
          message: `Response status:${error.response.status}`,
          extra: {
            status: error.response.status,
          },
        });
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    async (config: RequestOptions) => {
      const url = String(config.url ?? '');
      const isAuthHandshakeRequest =
        url.includes('/api/v1/auth/sessions') ||
        url.includes('/api/v1/auth/login-codes') ||
        url.includes('/api/v1/auth/token-refreshes');

      if (!isAuthHandshakeRequest && getUserAccessToken()) {
        await ensureFreshUserAccessToken();
      }

      const token = getUserAccessToken();

      return {
        ...config,
        headers: {
          ...config.headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      const data = response.data as ResponseStructure | undefined;
      if (response.status >= 400) {
        void reportMonitoringEvent({
          eventType: 'http_error',
          eventName: 'request_http_error',
          level: 'error',
          page: history.location.pathname,
          route: String(response.config?.url ?? history.location.pathname),
          message: `HTTP ${response.status}`,
        });
      }
      if (data && (data.code === 40100 || data.code === 40101)) {
        handleUserSessionExpired();
      }
      if (data && data.code !== 0) {
        throw createBizError(data.code, data.message, data.data);
      }
      return response;
    },
  ],
};
